/**
 * Hero assembly — one zod props schema shared by the HeroSquare (1080x1080) and HeroVertical (1080x1920)
 * compositions. Generated clips + founder bookends + narration + music + SFX + captions → finished ad master.
 *
 * Creative spec: SloaneVault Foundations docs 33/34/35 (hard cuts by default, at most two "thread wipes",
 * a luminous Lochmara-blue demand thread that can pass behind the subject via an occlusion matte, Oswald
 * kinetic type, lower-third captions, optional product-screen inserts, a full-frame logo close with its own
 * audio under which narration and music are ducked to silence).
 */
import { z } from 'zod';
import { CaptionWordSchema } from '../schemas';

export const HERO_FPS = 30;
/** Lochmara blue — the demand thread, the accent word, the current caption word. */
export const HERO_ACCENT = '#0274BE';
export const HERO_ASPECTS = ['1:1', '9:16'] as const;
export type HeroAspect = (typeof HERO_ASPECTS)[number];
export const HERO_DIMENSIONS: Record<HeroAspect, { width: number; height: number }> = {
  '1:1': { width: 1080, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
};
/** Composition ids registered in Root.tsx; each one pins its aspect regardless of `props.aspect`. */
export const HERO_COMPOSITION_IDS: Record<HeroAspect, string> = {
  '1:1': 'HeroSquare',
  '9:16': 'HeroVertical',
};
/** The aspect a composition id forces, or undefined for a composition that honours `props.aspect`. */
export function aspectForCompositionId(compositionId: string | undefined): HeroAspect | undefined {
  for (const aspect of HERO_ASPECTS) {
    if (HERO_COMPOSITION_IDS[aspect] === compositionId) return aspect;
  }
  return undefined;
}
/** A thread wipe overlaps the outgoing and incoming clips by this much. */
export const THREAD_WIPE_SECONDS = 0.5;
/** The spec allows one or two thread wipes per ad; everything else is a hard cut. */
export const MAX_THREAD_WIPES = 2;
/** Vertical masters reserve the top/bottom 15% for platform UI chrome. */
export const VERTICAL_SAFE_ZONE = 0.15;

export const HeroThreadPointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});

export const HeroThreadSchema = z.object({
  /** Spline control points in normalized 0–1 frame coordinates (≥ 2). */
  points: z.array(HeroThreadPointSchema).min(2),
  /** Seconds into the clip when the thread enters (default 0). */
  enterAt: z.number().min(0).optional(),
  /** Seconds into the clip by which the thread has fully left (default: clip end). */
  exitAt: z.number().min(0).optional(),
});

export const HeroClipSchema = z.object({
  url: z.string().min(1),
  durationSec: z.number().positive(),
  trimStartSec: z.number().min(0).optional(),
  /** Grayscale occlusion matte video (white = subject) with the clip's own length/size. */
  matteUrl: z.string().optional(),
  thread: HeroThreadSchema.optional(),
  transitionIn: z.enum(['cut', 'thread-wipe']).default('cut'),
});

export const HeroBookendSchema = z.object({
  url: z.string().min(1),
  durationSec: z.number().positive(),
});

export const HeroPhraseSchema = z.object({
  text: z.string().min(1),
  /** The word rendered in HERO_ACCENT (matched case-insensitively, punctuation-tolerant). */
  accentWord: z.string().optional(),
  atSec: z.number().min(0),
  durationSec: z.number().min(0.75).max(6).default(2),
});

export const HeroSfxSchema = z.object({
  url: z.string().min(1),
  atSec: z.number().min(0),
  /** Remotion's audio volume contract is 0..1 — a hit that needs to be louder must be pre-normalized in the file. */
  gain: z.number().min(0).max(1).default(1),
});

export const HeroProductInsertSchema = z.object({
  imageUrl: z.string().min(1),
  atSec: z.number().min(0),
  durationSec: z.number().min(0.5).max(4).default(1.5),
});

export const HeroNarrationSegmentSchema = z.object({
  startSec: z.number().min(0),
  endSec: z.number().min(0),
});

export const HeroPropsSchema = z.object({
  aspect: z.enum(HERO_ASPECTS).default('1:1'),
  clips: z.array(HeroClipSchema).min(1),
  /** Level of the generated clips' own audio track (LTX ambience). Off by default — narration/music carry the ad. */
  clipVolume: z.number().min(0).max(1).default(0),
  /** Founder bookends (HeyGen output). Conform to 30 fps before use — see docs/hero-assembly.md. */
  openClip: HeroBookendSchema.optional(),
  closeClip: HeroBookendSchema.optional(),
  /** Full-frame brand close with its own audio; narration/music duck to silence underneath. */
  logoCloseClip: HeroBookendSchema.optional(),
  logoCloseFit: z.enum(['contain', 'cover']).default('contain'),
  logoCloseBackground: z.string().default('#FFFFFF'),
  phrases: z.array(HeroPhraseSchema).default([]),
  /** CaptionSchema-compatible word timings, relative to content start (ms). */
  captions: z.object({ words: z.array(CaptionWordSchema) }).optional(),
  narrationUrl: z.string().optional(),
  /** When omitted and narrationUrl is set, narration is assumed to span the whole content. */
  narrationSegments: z.array(HeroNarrationSegmentSchema).optional(),
  narrationVolume: z.number().min(0).max(1).default(1),
  musicUrl: z.string().optional(),
  /** Bed level while no narration plays; ducked to MUSIC_DUCKED_RATIO of this under narration. */
  musicVolume: z.number().min(0).max(1).default(0.35),
  sfx: z.array(HeroSfxSchema).default([]),
  productInserts: z.array(HeroProductInsertSchema).default([]),
});

export type HeroProps = z.infer<typeof HeroPropsSchema>;
export type HeroPropsInput = z.input<typeof HeroPropsSchema>;
export type HeroClip = z.infer<typeof HeroClipSchema>;
export type HeroThread = z.infer<typeof HeroThreadSchema>;
export type HeroPhrase = z.infer<typeof HeroPhraseSchema>;
export type HeroBookend = z.infer<typeof HeroBookendSchema>;
export type HeroSfx = z.infer<typeof HeroSfxSchema>;
export type HeroProductInsert = z.infer<typeof HeroProductInsertSchema>;

export const secondsToFrames = (seconds: number, fps: number = HERO_FPS): number => Math.round(seconds * fps);

/**
 * Invariants the zod object cannot express (kept out of the schema so the Studio props editor stays fully
 * editable). Returns human-readable problems; empty means valid.
 */
export function validateHeroProps(props: HeroProps, fps: number = HERO_FPS): string[] {
  const problems: string[] = [];
  const wipeFrames = secondsToFrames(THREAD_WIPE_SECONDS, fps);

  const wipes = props.clips.filter((c, i) => c.transitionIn === 'thread-wipe' && (i > 0 || props.openClip)).length;
  if (wipes > MAX_THREAD_WIPES) {
    problems.push(`at most ${MAX_THREAD_WIPES} thread wipes are allowed (got ${wipes})`);
  }

  const chain: Array<{ label: string; frames: number; wipeIn: boolean }> = [];
  if (props.openClip) chain.push({ label: 'openClip', frames: secondsToFrames(props.openClip.durationSec, fps), wipeIn: false });
  props.clips.forEach((c, i) => {
    chain.push({
      label: `clips[${i}]`,
      frames: secondsToFrames(c.durationSec, fps),
      wipeIn: c.transitionIn === 'thread-wipe' && chain.length > 0,
    });
  });
  if (props.closeClip) chain.push({ label: 'closeClip', frames: secondsToFrames(props.closeClip.durationSec, fps), wipeIn: false });
  chain.forEach((seg, i) => {
    const overlap = (seg.wipeIn ? wipeFrames : 0) + (chain[i + 1]?.wipeIn ? wipeFrames : 0);
    if (seg.frames <= overlap) {
      problems.push(`${seg.label} is too short (${seg.frames} frames) for its thread wipe overlap (${overlap} frames)`);
    }
  });

  const sorted = [...props.phrases].sort((a, b) => a.atSec - b.atSec);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    if (sorted[i]!.atSec < prev.atSec + prev.durationSec) {
      problems.push(`phrases overlap: "${prev.text}" and "${sorted[i]!.text}" — never more than one belief phrase on screen`);
    }
  }

  props.clips.forEach((c, i) => {
    if (c.thread) {
      const enter = c.thread.enterAt ?? 0;
      const exit = c.thread.exitAt ?? c.durationSec;
      if (exit <= enter) problems.push(`clips[${i}].thread: exitAt must be after enterAt`);
      if (exit > c.durationSec + 1e-6) problems.push(`clips[${i}].thread: exitAt exceeds the clip duration`);
    }
  });

  (props.narrationSegments ?? []).forEach((s, i) => {
    if (s.endSec <= s.startSec) problems.push(`narrationSegments[${i}]: endSec must be after startSec`);
  });

  return problems;
}

/** Sample-ready defaults; the referenced media is what scripts/hero-render-sample.ts stages. */
export function getDefaultHeroProps(aspect: HeroAspect): HeroProps {
  const square = aspect === '1:1';
  return HeroPropsSchema.parse({
    aspect,
    clips: [
      {
        url: square ? 'uploads/hero-sample/clip-01.mp4' : 'uploads/hero-sample/clip-vert-01.mp4',
        durationSec: 5,
        matteUrl: square ? 'uploads/hero-sample/clip-01-matte.mp4' : undefined,
        thread: {
          points: [
            { x: 0, y: 0.9 },
            { x: 0.3, y: 0.62 },
            { x: 0.55, y: 0.7 },
            { x: 0.82, y: 0.35 },
            { x: 1, y: 0.2 },
          ],
          enterAt: 0.4,
          exitAt: 4.2,
        },
      },
    ],
    phrases: [{ text: 'THE BID WENT QUIET.', accentWord: 'QUIET.', atSec: 0.6, durationSec: 2 }],
    sfx: [],
    productInserts: [],
  } satisfies HeroPropsInput);
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
